import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { useModalHook } from "../../../../services/hooks/useModal";
import { useAppSelector } from "../../../../services/hooks/useReduxHooks";
import { PagesEnum } from "../../../../utils/enums";
import { isArabicLanguage } from "../../../../utils/help/localization/localization";
import { getPageLink } from "../../../../utils/help/navigation/navigation";
import { RestaurantImage } from "../../../../utils/types";
import { PImage } from "../../../shared/PImage/PImage";
import styles from "./RestaurantBannerSlider.module.scss";

export const RestaurantBannerSlider = (props: {
  restaurantImages: RestaurantImage[];
}) => {
  const isMobile = useAppSelector(
    (state) => state.globalComponentsToggleSlice.isMobile
  );
  const { renderWebViewItem } = useModalHook();

  const router = useRouter();
  const sliderImageUrls = props.restaurantImages;
  const pageWidth = window.innerWidth;
  const lastSlideIndex = sliderImageUrls.length - 1;
  const [currentSlide, setCurrentSlide] = useState(0);
  const targetRef = useRef<any>(null);
  const [width, setWidth] = useState(0);
  const autoSlideLimit = isMobile ? 1 : 3;

  const selectBanner = (itemId?: number) => {
    if (itemId) {
      if (isMobile) {
        return router.push(
          `${
            getPageLink(router, PagesEnum.Item, {
              as: itemId + "",
            }).as
          }`
        );
      } else {
        return renderWebViewItem(itemId);
      }
    }
  };

  useEffect(() => {
    if (targetRef.current) {
      setWidth(targetRef.current.clientWidth);
    }
    if (sliderImageUrls.length > autoSlideLimit) {
      const timeout = currentSlide === 0 ? 1 : 5000;
      const timer = setTimeout(() => {
        setCurrentSlide((prevSlide) =>
          prevSlide === sliderImageUrls.length ? 0 : prevSlide + 1
        );
      }, timeout);

      return () => clearTimeout(timer);
    }
  }, [currentSlide, sliderImageUrls.length]);

  const OneSlide = (props: {
    index: number;
    url: string;
    aspectRatio?: string;
    itemId?: number;
  }) => {
    return (
      <div
        key={props.index}
        className={styles.banner_slide}
        data-testid={`restaurant-banner-slide-${props.index}`}
        ref={targetRef}
        onClick={() => {
          if (
            sliderImageUrls.length <= autoSlideLimit ||
            currentSlide === props.index + 1
          ) {
            selectBanner(props.itemId);
          }

          if (sliderImageUrls.length > autoSlideLimit && isMobile) {
            setCurrentSlide(props.index + 1);
          }
        }}
      >
        <div
          className={styles.slide_mini}
          style={{ aspectRatio: props.aspectRatio }}
        >
          <PImage
            src={props.url}
            logoFallback={false}
            alt="Restaurant banner"
            fill
            style={{ objectFit: "cover" }}
          />
        </div>
      </div>
    );
  };

  if (sliderImageUrls.length === 0) {
    return <></>;
  }

  return (
    <div className={styles.banner_main} data-testid="restaurant-banner-slider">
      <div className={`${styles.banner_wrapper}`}>
        <div
          className={`${styles.banner_track} ${
            currentSlide === 0 ? styles.inactive : ""
          }`}
          style={{
            transform: isArabicLanguage()
              ? `translateX(${currentSlide * width}px)`
              : `translateX(-${currentSlide * width}px)`,
            width: pageWidth * (sliderImageUrls.length * 3),
          }}
        >
          {" "}
          {isArabicLanguage()}{" "}
          {sliderImageUrls.length > autoSlideLimit ? (
            <>
              <OneSlide
                index={lastSlideIndex}
                url={sliderImageUrls[lastSlideIndex].url}
                aspectRatio={sliderImageUrls[lastSlideIndex].aspectRatio}
                itemId={sliderImageUrls[lastSlideIndex].itemId}
              />
              {sliderImageUrls.map((image, index) => (
                <OneSlide
                  key={image.itemId || index}
                  index={index}
                  url={image.url}
                  aspectRatio={image.aspectRatio}
                  itemId={image.itemId}
                />
              ))}
              {sliderImageUrls.map((image, index) => (
                <OneSlide
                  key={image.itemId || index}
                  index={index}
                  url={image.url}
                  aspectRatio={image.aspectRatio}
                  itemId={image.itemId}
                />
              ))}
            </>
          ) : (
            <>
              {sliderImageUrls.map((image, index) => (
                <OneSlide
                  key={image.itemId || index}
                  index={index}
                  url={image.url}
                  aspectRatio={image.aspectRatio}
                  itemId={image.itemId}
                />
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
